import { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { Row, Col, Form, Button, Spinner, Card, Badge, Modal } from "react-bootstrap";
import { useForm, Controller } from "react-hook-form";
import AsyncSelect from 'react-select/async';
import visitasService from "../../../api/visitas/visitasService";
import visitasOrigensService from "../../../api/visitas/visitasOrigensService";
import visitasViasService from "../../../api/visitas/visitasViasService";
import prestadoresService from "../../../api/pessoas/prestadoresService";
import doencasService from "../../../api/programas/doencasService";
import Swal from 'sweetalert2';
import { FaSave, FaEye, FaTrash } from "react-icons/fa";
import { getCurrentUser, formataDataHoraExibicao } from '../../../common/utils'; 

const VisitasEdit = ({ id, programa_id, onSuccess, onCancel }) => {
    const [loading, setLoading] = useState(false);
    const [dropdowns, setDropdowns] = useState({
        origens: [],
        vias: [],
        doencas: [],
        visitas_relacionadas: 0,
        visitas_historico: [],
        medico_detalhes: null
    });
    const [showHistoryModal, setShowHistoryModal] = useState(false);

    const { register, handleSubmit, setValue, control, formState: { errors } } = useForm();
    const dados_usuario = getCurrentUser();
    const currentProgramaId = programa_id || dados_usuario['ambiente_programa_id'];


    const carregarDropdowns = useCallback(async (progId) => {
        try {
            const [respOrigens, respVias, respDoencas] = await Promise.all([
                visitasOrigensService.getAll({ programa_id: progId }),
                visitasViasService.getAll({ programa_id: progId }),
                doencasService.getAll({ programa_id: progId }) 
            ]);

            const listaDoencas = (respDoencas.data.results || respDoencas.data).map(item => ({
                id: item.dados_doenca.id,
                nome: item.dados_doenca.nome
            }));

            setDropdowns(prev => ({
                ...prev,
                origens: respOrigens.data,
                vias: respVias.data,
                doencas: listaDoencas
            }));
        } catch (error) {
            console.error("Erro ao carregar dados auxiliares", error);
        }
    }, []);

    const carregarMedicos = useCallback(async (inputValue) => {
        if (!currentProgramaId) return [];
        try {
            const params = {
                nome: inputValue,
                programa_id: currentProgramaId,
                categoria: 'medicos|multiprofissionais',
                is_active: 1,
                tipo_usuario: dados_usuario.tipo_usuario
            };
            const response = await prestadoresService.getAll(params);
            
            return response.data.results.map(medico => ({
                value: medico.id,
                label: `${medico.nome} (${medico.registro || 'S/CRM'})`
            }));
        } catch (error) {
            console.error("Erro ao buscar médicos", error);
            return [];
        }
    }, [currentProgramaId, dados_usuario.tipo_usuario]);

    const carregarHistoricoMedico = useCallback(async (medicoId) => {
        if (!medicoId || !currentProgramaId) return;
        try {
            const response = await visitasService.getAll({ programa_id: currentProgramaId, cliente_id: medicoId });
            const visitas = response.data.results || [];
            
            // Busca detalhes do médico
            const respMedico = await prestadoresService.get(medicoId, { programa_id: currentProgramaId });
            
            setDropdowns(prev => ({
                ...prev,
                visitas_relacionadas: visitas.length,
                visitas_historico: visitas,
                medico_detalhes: respMedico.data
            }));
        } catch (error) {
            console.error("Erro ao carregar histórico do médico", error);
        }
    }, [currentProgramaId]);

    const carregarDadosEdicao = useCallback(async (registroId) => {
        try {
            const response = await visitasService.get(registroId);
            const dados = response.data;
            
            if (dados.medico) {
                setValue('medico', { value: dados.medico, label: dados.medico_nome });
                carregarHistoricoMedico(dados.medico);
            }

            setValue('data', dados.data ? dados.data.substring(0, 16) : '');
            setValue('origem', dados.origem);
            setValue('via', dados.via);
            setValue('patologia', dados.patologia);
            setValue('documentos_solicitados', dados.documentos_solicitados);
            setValue('descricao', dados.descricao);
            setValue('observacoes', dados.observacoes);

            setValue('informacoes_relevantes', dados.informacoes_relevantes);
            setValue('farmacovigilancia', dados.farmacovigilancia);
            setValue('paciente_suspeito', dados.paciente_suspeito);
            setValue('paciente_diagnosticado', dados.paciente_diagnosticado);
            setValue('paciente_em_crise', dados.paciente_em_crise);
            setValue('usou_usando_medicacao', dados.usou_usando_medicacao);
            setValue('com_prescricao', dados.com_prescricao);
            setValue('exames_solicitados_psd', dados.exames_solicitados_psd);

        } catch (error) {
            console.error("Erro ao carregar registro", error);
            Swal.fire('Erro', 'Não foi possível carregar os dados.', 'error');
        }
    }, [setValue, carregarHistoricoMedico]);

    useEffect(() => {
        const loadInitialData = async () => {
            if (currentProgramaId) {
                await carregarDropdowns(currentProgramaId);
            }
            if (id) {
                await carregarDadosEdicao(id);
            }
        };

        loadInitialData();
    }, [id, currentProgramaId, carregarDropdowns, carregarDadosEdicao]);

    const handleDelete = async () => {
        if (!id) return;
        
        Swal.fire({
            title: 'Excluir Visita',
            text: 'Tem certeza que deseja excluir esta visita? Esta ação não pode ser desfeita.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Sim, excluir!',
            cancelButtonText: 'Cancelar'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await visitasService.delete(id);
                    Swal.fire('Excluído!', 'A visita foi excluída com sucesso.', 'success');
                    if (onSuccess) onSuccess();
                } catch (error) {
                    console.error("Erro ao excluir", error);
                    Swal.fire('Erro', 'Não foi possível excluir a visita.', 'error');
                }
            }
        });
    };

    const onSubmit = async (data) => {
        setLoading(true);
        const payload = { 
            ...data, 
            medico: data.medico?.value,
            programa: currentProgramaId 
        };
        
        try {
            if (id) {
                await visitasService.update(id, payload);
                Swal.fire('', '', 'success');
            } else {
                await visitasService.create(payload);
                Swal.fire('', '', 'success');
            }
            if(onSuccess) onSuccess();
        } catch (error) {
            console.error(error);
            Swal.fire('Erro', 'Erro ao salvar visita.', 'error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Form onSubmit={handleSubmit(onSubmit)}>
            <h6 className="text-muted mb-3">Dados Gerais</h6>
            <Row>
                <Col md={6}>
                    <Form.Group className="mb-3">
                        <div className="d-flex justify-content-between align-items-center">
                            <Form.Label>Médico <span className="text-danger">*</span></Form.Label>
                            {dropdowns.visitas_relacionadas > 0 && (
                                <div className="d-flex align-items-center gap-1">
                                    <Badge bg="primary" pill className="mb-0">
                                        {dropdowns.visitas_relacionadas} visitas relacionadas
                                    </Badge>
                                    <Button 
                                        variant="link" 
                                        size="sm" 
                                        className="p-0 text-primary" 
                                        title="Ver histórico"
                                        onClick={() => setShowHistoryModal(true)}
                                    >
                                        <FaEye />
                                    </Button>
                                </div>
                            )}
                        </div>
                        <Controller
                            name="medico"
                            control={control}
                            rules={{ required: "Selecione um médico" }}
                            render={({ field }) => (
                                <AsyncSelect
                                    {...field}
                                    cacheOptions
                                    defaultOptions
                                    loadOptions={carregarMedicos}
                                    placeholder="Digite o nome ou CRM..."
                                    noOptionsMessage={() => "Nenhum médico encontrado"}
                                    classNamePrefix="select"
                                    onChange={(val) => {
                                        field.onChange(val);
                                        if (val) carregarHistoricoMedico(val.value);
                                        else setDropdowns(prev => ({ ...prev, visitas_relacionadas: 0, medico_detalhes: null }));
                                    }}
                                />
                            )}
                        />
                        {errors.medico && <span className="text-danger small">{errors.medico.message}</span>}
                        {dropdowns.medico_detalhes && (
                            <div className="mt-1 small text-muted">
                                <strong>Registro:</strong> {dropdowns.medico_detalhes.registro || '-'} | <strong>UF:</strong> {dropdowns.medico_detalhes.registro_uf || '-'}
                            </div>
                        )}
                    </Form.Group>
                </Col>
                <Col md={6}>
                    <Form.Group className="mb-3">
                        <Form.Label>Data da Visita <span className="text-danger">*</span></Form.Label>
                        <Form.Control type="datetime-local" {...register("data", { required: true })} isInvalid={!!errors.data} />
                    </Form.Group>
                </Col>
                <Col md={4}>
                    <Form.Group className="mb-3">
                        <Form.Label>Origem <span className="text-danger">*</span></Form.Label>
                        <Form.Select {...register("origem", { required: true })} isInvalid={!!errors.origem}>
                            <option value="">Selecione...</option>
                            {dropdowns.origens.map(o => <option key={o.id} value={o.id}>{o.nome}</option>)}
                        </Form.Select>
                    </Form.Group>
                </Col>
                <Col md={4}>
                    <Form.Group className="mb-3">
                        <Form.Label>Via de Contato <span className="text-danger">*</span></Form.Label>
                        <Form.Select {...register("via", { required: true })} isInvalid={!!errors.via}>
                            <option value="">Selecione...</option>
                            {dropdowns.vias.map(v => <option key={v.id} value={v.id}>{v.nome}</option>)}
                        </Form.Select>
                    </Form.Group>
                </Col>
                <Col md={4}>
                    <Form.Group className="mb-3">
                        <Form.Label>Patologia</Form.Label>
                        <Form.Select {...register("patologia")}>
                            <option value="">Selecione...</option>
                            {dropdowns.doencas.map(d => <option key={d.id} value={d.id}>{d.nome}</option>)}
                        </Form.Select>
                    </Form.Group>
                </Col>
            </Row>

            <hr />
            <h6 className="text-muted mb-3">Checklist de Informações</h6>
            <Card className="bg-light border-0 mb-3">
                <Card.Body>
                    <Row>
                        <Col md={3} className="mb-2"><Form.Check type="switch" label="Info. Relevantes" {...register("informacoes_relevantes")} /></Col>
                        <Col md={3} className="mb-2"><Form.Check type="switch" label="Farmacovigilância" {...register("farmacovigilancia")} /></Col>
                        <Col md={3} className="mb-2"><Form.Check type="switch" label="Paciente Suspeito" {...register("paciente_suspeito")} /></Col>
                        <Col md={3} className="mb-2"><Form.Check type="switch" label="Paciente Diagnosticado" {...register("paciente_diagnosticado")} /></Col>
                        <Col md={3} className="mb-2"><Form.Check type="switch" label="Paciente em Crise" {...register("paciente_em_crise")} /></Col>
                        <Col md={3} className="mb-2"><Form.Check type="switch" label="Usou/Usando Med." {...register("usou_usando_medicacao")} /></Col>
                        <Col md={3} className="mb-2"><Form.Check type="switch" label="Com Prescrição" {...register("com_prescricao")} /></Col>
                        <Col md={3} className="mb-2"><Form.Check type="switch" label="Exames Solicitados (PSD)" {...register("exames_solicitados_psd")} /></Col>
                    </Row>
                </Card.Body>
            </Card>

            <Row>
                <Col md={12}>
                    <Form.Group className="mb-3">
                        <Form.Label>Documentos Solicitados (Descrever)</Form.Label>
                        <Form.Control as="textarea" rows={2} {...register("documentos_solicitados")} placeholder="Ex: Agendamento de visita no HC para exame..." />
                    </Form.Group>
                </Col>
                <Col md={12}>
                    <Form.Group className="mb-3">
                        <Form.Label>Descrição da Visita <span className="text-danger">*</span></Form.Label>
                        <Form.Control as="textarea" rows={3} {...register("descricao", { required: true })} isInvalid={!!errors.descricao} />
                    </Form.Group>
                </Col>
                <Col md={12}>
                    <Form.Group className="mb-3">
                        <Form.Label>Observações Gerais</Form.Label>
                        <Form.Control as="textarea" rows={2} {...register("observacoes")} />
                    </Form.Group>
                </Col>
            </Row>

            <div className="text-end border-top pt-3 mt-2 d-flex justify-content-between align-items-center">
                <div>
                    {id && (
                        <Button variant="outline-danger" onClick={handleDelete} disabled={loading}>
                            <FaTrash className="me-1"/> Excluir
                        </Button>
                    )}
                </div>
                <div>
                    <Button variant="secondary" className="me-2" onClick={onCancel} disabled={loading}>Cancelar</Button>
                    <Button variant="success" type="submit" disabled={loading}>
                        {loading ? <Spinner size="sm" animation="border" /> : <><FaSave className="me-1"/> Salvar</>}
                    </Button>
                </div>
            </div>

            {/* Modal de Histórico (reusado do componente pai) */}
            <Modal show={showHistoryModal} onHide={() => setShowHistoryModal(false)} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>Histórico de Visitas: {dropdowns.medico_detalhes?.nome}</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                    {(dropdowns.visitas_historico || []).length === 0 ? (
                        <div className="text-center text-muted py-3">Nenhuma visita encontrada.</div>
                    ) : (
                        <div className="history-timeline">
                            {(dropdowns.visitas_historico || []).map((h) => (
                                <Card key={h.id} className="mb-3 border-0 bg-light">
                                    <Card.Header className="bg-white border-bottom-0 d-flex justify-content-between align-items-center">
                                        <span className="fw-bold text-dark small">{formataDataHoraExibicao(h.data)}</span>
                                        <div>
                                            <Badge bg="secondary" size="sm" className="me-1">{h.origem_nome}</Badge>
                                            <Badge bg="info" size="sm">{h.via_nome}</Badge>
                                        </div>
                                    </Card.Header>
                                    <Card.Body className="pt-0 pb-2">
                                        <hr className="mt-0 mb-2" />
                                        <div className="small fw-bold text-muted mb-1">Checklist:</div>
                                        <div className="d-flex flex-wrap gap-1 mb-2">
                                            {h.informacoes_relevantes && <Badge bg="warning" text="dark" className="x-small">Info. Relevantes</Badge>}
                                            {h.farmacovigilancia && <Badge bg="danger" className="x-small">Farmacovigilância</Badge>}
                                            {h.paciente_suspeito && <Badge bg="outline-secondary" className="x-small text-muted">Pac. Suspeito</Badge>}
                                            {h.paciente_diagnosticado && <Badge bg="outline-secondary" className="x-small text-muted">Pac. Diagnosticado</Badge>}
                                            {h.paciente_em_crise && <Badge bg="outline-secondary" className="x-small text-muted">Pac. Crise</Badge>}
                                            {h.usou_usando_medicacao && <Badge bg="outline-secondary" className="x-small text-muted">Usou/Usando Med.</Badge>}
                                            {h.com_prescricao && <Badge bg="outline-secondary" className="x-small text-muted">Com Prescrição</Badge>}
                                            {h.exames_solicitados_psd && <Badge bg="outline-secondary" className="x-small text-muted">Exames PSD</Badge>}
                                        </div>
                                        <div className="small fw-bold text-muted">Descrição:</div>
                                        <div className="bg-white p-2 rounded border x-small">{h.descricao}</div>
                                    </Card.Body>
                                </Card>
                            ))}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowHistoryModal(false)}>Fechar</Button>
                </Modal.Footer>
            </Modal>
        </Form>
    );
};
VisitasEdit.propTypes = {
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    programa_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onSuccess: PropTypes.func,
    onCancel: PropTypes.func
};

export default VisitasEdit;
